import React, { memo, useEffect, useState } from 'react'
import { FieldErrors, useWatch } from 'react-hook-form'
import capitalize from 'lodash/capitalize'
import ControlledAutocompleteFacility from 'transfix-form/components/ControlledAutocompleteFacility'
import ControlledCheckbox from 'transfix-form/components/ControlledCheckbox'
import ControlledCommodityTypeSelect from 'transfix-form/components/ControlledCommodityTypeSelect'
import ControlledSelect from 'transfix-form/components/ControlledSelect'
import ControlledTextArea from 'transfix-form/components/ControlledTextArea'
import ControlledTextInput from 'transfix-form/components/ControlledTextInput'
import { FormIf } from 'transfix-form/components/FormIf'
import { IConstructedStopFacility } from 'transfix-ui/components/AutocompleteBase/types'
import Grid from 'transfix-ui/core/Grid'
import Typography from 'transfix-ui/core/Typography'
import {
  IShipmentStopAppointmentTypes,
  IFacilityAppointmentTypeEnum,
  Maybe,
} from 'types/graphqlTypes'
import FacilityDetails from 'apps/marketplace/components/FacilityDetails'
import useShipperFacility from 'apps/marketplace/hooks/useShipperFacility/useShipperFacility'
import useShipperFacilityVendors from 'apps/marketplace/hooks/useShipperFacilityVendors/useShipperFacilityVendors'
import DISPLAY from 'constants/display'
import { useCreateShipmentFormContext } from '../../../context'
import { TAutocompleteValueSchema } from '../../../schema/AutocompleteValueSchema'
import {
  getAutocompleteErrorMessage,
  getStopErrorMessage,
  getShipmentStopFieldError,
} from '../../../utils'
import AppointmentDateWarnings from './AppointmentDateWarnings'
import ControlledDateTimeInput from './ControlledDateTimeInput'
import StopButtons from './StopButtons'
import VendorSection from './VendorSection'
import { APPOINTMENT_TYPE_OPTIONS } from './constants'

export interface IStopFormSectionProps {
  /**
   * The index value for the stop in shipment.stops array
   * @example 1
   */
  stopIndex: number
  /**
   * @example '124212'
   */
  shipperApplicationId: string
  /**
   * @example 'pickup'
   */
  stopType: string
  /**
   * @example 2
   */
  stopSequence: number
  /**
   * @example true
   */
  dropTrailer?: boolean
  /**
   * @default true
   */
  includeAddButton?: boolean
  /**
   * @default false
   */
  includeDeleteButton?: boolean
  stopAppointmentType: 'pickup' | 'delivery'
  /**
   * Function called when user clicks, 'Add Pickup Stop' button
   */
  onAddStop?: () => void
  /**
   * Function called when user clicks, 'Delete Pickup Stop' button
   */
  onDeleteStop?: (index: number) => void
}

const StopFormSection = memo<IStopFormSectionProps>(
  ({
    dropTrailer = false,
    includeAddButton = true,
    includeDeleteButton = false,
    onAddStop,
    onDeleteStop,
    shipperApplicationId,
    stopAppointmentType,
    stopIndex,
    stopSequence,
    stopType,
  }) => {
    const parentKey = `shipment.stops[${stopIndex}]`
    const [inferredVendorId, setInferredVendorId] = useState<string | null>(null)
    /**
     * Form Context
     */
    const { control, errors, register, setValue } = useCreateShipmentFormContext()
    const shipperFacility: Maybe<TAutocompleteValueSchema> = useWatch({
      control,
      name: `${parentKey}.shipperFacility`,
    })
    const selectedVendorId: string | undefined = useWatch({
      control,
      name: `${parentKey}.vendorId`,
    })
    const { id: shipperFacilityUuid } = shipperFacility || {}
    const shipperFacilityState = useShipperFacility({
      shipperFacilityUuid,
    })
    const { facilityUuid } = shipperFacilityState

    const shipperFacilityVendorsState = useShipperFacilityVendors({
      facilityUuid,
      shipperFacilityUuid,
    })
    const { shipperFacilityVendorData } = shipperFacilityVendorsState

    /**
     * Errors
     */
    const { shipment: shipmentErrors } = errors || {}
    const stopErrors = getShipmentStopFieldError(shipmentErrors, stopIndex)
    const { shipperFacility: sfErrror } = stopErrors || {}
    const shipperFacilityError = sfErrror as FieldErrors<TAutocompleteValueSchema>

    useEffect(() => {
      if (shipperFacilityVendorData && shipperFacilityVendorData.length === 1) {
        // If there is only one vendor, set inferred vendor id
        setInferredVendorId(shipperFacilityVendorData[0].vendorId)
      } else if (!shipperFacilityVendorData || shipperFacilityVendorData.length === 0) {
        // Reset vendor id if vendor array is empty
        setValue(`${parentKey}.vendorId`, null)
      }
    }, [parentKey, setValue, shipperFacilityVendorData])

    /*
     * Set Stop Appointment Type based on Facility's Appointment Type
     */
    const changeFacilityHandler: (value: IConstructedStopFacility | null) => void = newFacility => {
      if (newFacility !== null) {
        const {
          deliveryAppointmentType: facilityDeliveryAppointmentType,
          pickupAppointmentType: facilityPickupAppointmentType,
        } = newFacility
        const facilityAppointmentType =
          stopAppointmentType === 'pickup'
            ? facilityPickupAppointmentType
            : facilityDeliveryAppointmentType
        let appointmentType = IShipmentStopAppointmentTypes.appointment
        if (
          facilityAppointmentType === IFacilityAppointmentTypeEnum.fcfs ||
          facilityAppointmentType === IFacilityAppointmentTypeEnum.notification_window
        ) {
          appointmentType = IShipmentStopAppointmentTypes.range
        }
        setValue(`${parentKey}.appointmentType`, appointmentType)
      }
    }

    return (
      <Grid container spacing={1} data-test='stop-form-section'>
        <Grid item xs={12}>
          <Typography variant='h3'>{`${capitalize(stopType)} ${stopSequence}`}</Typography>
        </Grid>
        <input type='hidden' name={`${parentKey}.stopType`} value={stopType} ref={register()} />
        <input
          type='hidden'
          name={`${parentKey}.stopSequence`}
          value={stopSequence}
          ref={register()}
        />
        <Grid item xs={12} xl={6}>
          <ControlledAutocompleteFacility
            control={control}
            name={`${parentKey}.shipperFacility`}
            shipperApplicationId={shipperApplicationId}
            onChangeFacility={changeFacilityHandler}
            AutocompleteFacilityProps={{
              label: 'Facility',
              dataTest: 'shipper-facility-field',
              inputError: !!getAutocompleteErrorMessage(shipperFacilityError, 'id'),
              inputValidationMessage: getAutocompleteErrorMessage(shipperFacilityError, 'id'),
              required: true,
              size: 'small',
              showAddressDetail: true,
            }}
          />
          {shipperFacilityUuid && (
            <VendorSection
              control={control}
              errorMessage={getStopErrorMessage(stopErrors, 'vendorId')}
              inputName={`${parentKey}.vendorId`}
              shipperFacilityState={shipperFacilityState}
              shipperFacilityVendorsState={shipperFacilityVendorsState}
            />
          )}
          {shipperFacilityUuid && (
            <FacilityDetails
              shipperFacilityState={shipperFacilityState}
              shipperFacilityVendorsState={shipperFacilityVendorsState}
              selectedVendorId={selectedVendorId || inferredVendorId}
              showScheduler
            />
          )}
          <StopButtons
            stopIndex={stopIndex}
            stopType={stopType}
            shipperFacilityUuid={shipperFacilityUuid}
            includeAddButton={includeAddButton}
            includeDeleteButton={includeDeleteButton}
            onAddStop={onAddStop}
            onDeleteStop={onDeleteStop}
          />
        </Grid>
        <Grid item xs={12} xl={6}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <ControlledSelect
                control={control}
                name={`${parentKey}.appointmentType`}
                SelectProps={{
                  label: 'Appointment Type',
                  dataTest: 'appointment-type-field',
                  options: APPOINTMENT_TYPE_OPTIONS,
                  error: !!getStopErrorMessage(stopErrors, 'appointmentType'),
                  validationMessage: getStopErrorMessage(stopErrors, 'appointmentType'),
                  required: true,
                }}
              />
            </Grid>
            <ControlledDateTimeInput stopIndex={stopIndex} dateType='start' />
            <FormIf
              control={control}
              name={`${parentKey}.appointmentType`}
              when={IShipmentStopAppointmentTypes.range}>
              <ControlledDateTimeInput stopIndex={stopIndex} dateType='end' />
            </FormIf>
            <Grid item xs={12}>
              <ControlledCheckbox
                control={control}
                name={`${parentKey}.confirmed`}
                CheckboxProps={{
                  label: 'Confirmed',
                  dataTest: 'confirmed-checkbox',
                }}
              />
              <ControlledCheckbox
                control={control}
                name={`${parentKey}.dropTrailer`}
                defaultValue={dropTrailer}
                CheckboxProps={{
                  label: `${capitalize(stopType)} Drop Trailer`,
                  dataTest: 'drop-trailer-checkbox',
                }}
              />
            </Grid>
            <AppointmentDateWarnings
              stopIndex={stopIndex}
              stopType={stopType}
              shipperFacilityUuid={shipperFacilityUuid}
            />
            <Grid item xs={12}>
              <ControlledTextInput
                control={control}
                name={`${parentKey}.appointmentReference`}
                TextInputProps={{
                  label: 'Appointment Number',
                  dataTest: 'appointment-number-field',
                  placeholder: '#',
                  error: !!getStopErrorMessage(stopErrors, 'appointmentReference'),
                  validationMessage: getStopErrorMessage(stopErrors, 'appointmentReference'),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextInput
                control={control}
                name={`${parentKey}.confirmationReference`}
                TextInputProps={{
                  label: 'Confirmation Number',
                  dataTest: 'confirmation-number-field',
                  placeholder: '#',
                  error: !!getStopErrorMessage(stopErrors, 'confirmationReference'),
                  validationMessage: getStopErrorMessage(stopErrors, 'confirmationReference'),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextArea
                control={control}
                name={`${parentKey}.specialInstruction`}
                TextAreaProps={{
                  label: 'Special Instructions',
                  dataTest: 'special-instructions-field',
                  placeholder: `${capitalize(stopType)} Instructions`,
                  size: 'small',
                  invalid: !!getStopErrorMessage(stopErrors, 'specialInstruction'),
                }}
              />
            </Grid>
            <Grid item xs={12} xl={6}>
              <ControlledTextInput
                control={control}
                name={`${parentKey}.weight`}
                TextInputProps={{
                  label: 'Weight',
                  dataTest: 'weight-field',
                  type: 'number',
                  placeholder: 'lbs',
                  error: !!getStopErrorMessage(stopErrors, 'weight'),
                  validationMessage: getStopErrorMessage(stopErrors, 'weight'),
                }}
              />
            </Grid>
            <Grid item xs={12} xl={6}>
              <ControlledTextInput
                control={control}
                name={`${parentKey}.palletCount`}
                TextInputProps={{
                  label: 'Pallet Count',
                  dataTest: 'pallet-count-field',
                  type: 'number',
                  placeholder: '#',
                  error: !!getStopErrorMessage(stopErrors, 'palletCount'),
                  validationMessage: getStopErrorMessage(stopErrors, 'palletCount'),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledCommodityTypeSelect
                control={control}
                name={`${parentKey}.genericCommodityId`}
                SelectProps={{
                  label: 'Commodity Type',
                  dataTest: 'commodity-type-field',
                  placeholder: DISPLAY.empty,
                  error: !!getStopErrorMessage(stopErrors, 'genericCommodityId'),
                  validationMessage: getStopErrorMessage(stopErrors, 'genericCommodityId'),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextInput
                control={control}
                name={`${parentKey}.commodity`}
                TextInputProps={{
                  label: 'Commodity Description',
                  dataTest: 'commodity-field',
                  error: !!getStopErrorMessage(stopErrors, 'commodity'),
                  validationMessage: getStopErrorMessage(stopErrors, 'commodity'),
                  required: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextInput
                control={control}
                name={`${parentKey}.billOfLadingReference`}
                TextInputProps={{
                  label: 'BOL',
                  type: 'text',
                  dataTest: 'bol-field',
                  error: !!getStopErrorMessage(stopErrors, 'billOfLadingReference'),
                  validationMessage: getStopErrorMessage(stopErrors, 'billOfLadingReference'),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextInput
                control={control}
                name={`${parentKey}.poReference`}
                TextInputProps={{
                  label: 'PO',
                  type: 'text',
                  dataTest: 'po-field',
                  error: !!getStopErrorMessage(stopErrors, 'poReference'),
                  validationMessage: getStopErrorMessage(stopErrors, 'poReference'),
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )
  }
)

export default StopFormSection
